"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePlacesLib } from "@/components/providers/places-provider";
import { handleError } from "@/lib/handle-error";
import type { ParsedAddress } from "./address-types";
import { parseGooglePlace } from "./parse-google-place";

const DEBOUNCE_MS = 300;
const MIN_INPUT_LENGTH = 3;
const DEFAULT_REGION_CODES = ["us", "ca"];

export type AddressSuggestion = google.maps.places.AutocompleteSuggestion;

export interface AddressSuggestionsOptions {
  onChange: (displayValue: string, parsed: ParsedAddress | null) => void;
  regionCodes?: string[];
  onResolvingChange?: (resolving: boolean) => void;
}

function navigateSuggestions(
  event: React.KeyboardEvent,
  {
    count,
    activeIndex,
    setActiveIndex,
    setIsOpen,
    onSelect,
  }: {
    count: number;
    activeIndex: number;
    setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
    setIsOpen: (open: boolean) => void;
    onSelect: () => void;
  },
) {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    setActiveIndex((previous) => (previous < count - 1 ? previous + 1 : 0));
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    setActiveIndex((previous) => (previous > 0 ? previous - 1 : count - 1));
  } else if (event.key === "Enter" && activeIndex >= 0) {
    event.preventDefault();
    onSelect();
  } else if (event.key === "Escape") {
    setIsOpen(false);
  }
}

export function useAddressSuggestions({
  onChange,
  regionCodes = DEFAULT_REGION_CODES,
  onResolvingChange,
}: AddressSuggestionsOptions) {
  const onChangeRef = useRef(onChange);
  const onResolvingChangeRef = useRef(onResolvingChange);
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputAwaitingLibraryRef = useRef<string | null>(null);
  const placesLib = usePlacesLib();

  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isUnavailable, setIsUnavailable] = useState(false);

  useEffect(() => {
    onChangeRef.current = onChange;
    onResolvingChangeRef.current = onResolvingChange;
  }, [onChange, onResolvingChange]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!placesLib || input.length < MIN_INPUT_LENGTH) {
        setSuggestions([]);
        setIsOpen(false);
        return;
      }
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
      }
      try {
        const { suggestions: results } =
          await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            sessionToken: sessionTokenRef.current,
            includedRegionCodes: regionCodes,
            includedPrimaryTypes: ["geocode"],
          });
        setSuggestions(results);
        setIsOpen(results.length > 0);
        setActiveIndex(-1);
        setIsUnavailable(false);
      } catch (error) {
        setSuggestions([]);
        setIsOpen(false);
        setIsUnavailable(true);
        handleError(error, { display: "silent" });
      }
    },
    [placesLib, regionCodes],
  );

  const handleSelect = useCallback(async (suggestion: AddressSuggestion) => {
    const prediction = suggestion.placePrediction;
    if (!prediction) return;
    setIsOpen(false);
    setSuggestions([]);
    onResolvingChangeRef.current?.(true);
    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ["addressComponents", "formattedAddress", "location"],
      });
      const { parsed, display } = parseGooglePlace(place);
      onChangeRef.current(display, parsed);
    } catch (error) {
      handleError(error);
    } finally {
      sessionTokenRef.current = null;
      onResolvingChangeRef.current?.(false);
    }
  }, []);

  const debouncedFetch = useCallback(
    (input: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      inputAwaitingLibraryRef.current =
        !placesLib && input.length >= MIN_INPUT_LENGTH ? input : null;
      debounceRef.current = setTimeout(
        () => {
          void fetchSuggestions(input);
        },
        DEBOUNCE_MS,
      );
    },
    [fetchSuggestions, placesLib],
  );

  useEffect(() => {
    const input = inputAwaitingLibraryRef.current;
    if (!placesLib || input === null) return;
    inputAwaitingLibraryRef.current = null;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    void fetchSuggestions(input);
  }, [placesLib, fetchSuggestions]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isOpen || suggestions.length === 0) return;
      navigateSuggestions(event, {
        count: suggestions.length,
        activeIndex,
        setActiveIndex,
        setIsOpen,
        onSelect: () => handleSelect(suggestions[activeIndex]),
      });
    },
    [isOpen, suggestions, activeIndex, handleSelect],
  );

  return {
    suggestions,
    isUnavailable,
    isOpen,
    activeIndex,
    setActiveIndex,
    setIsOpen,
    debouncedFetch,
    handleSelect,
    handleKeyDown,
  };
}
